import { memo, useCallback, useMemo } from 'react';

import { useTrackOpenIntercomChat } from '@air/analytics';
import { AlertBanner } from '@air/primitive-alert-banner';
import { useAirModal } from '@air/provider-modal';

import { Routes } from '~/constants/routes';
import { NEW_TAB } from '~/constants/urls';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePlanContext } from '~/providers/PlanProvider';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';
import { useStorage } from '~/swr-hooks/subscriptions/useStorage';
import { canManageSubscription } from '~/utils/permissions/workspacePermissions';
import { PlansModal } from '../Modals/PlansModal/PlansModal';
import { useStorageLimitBannerDismiss } from './hooks/useStorageLimitBannerDismiss';

export const WorkspaceStorageStatusBanner = memo(() => {
  const { isEnterprisePlan, isBusinessPlan } = usePlanContext();
  const { members } = useWorkspaceMembers();
  const { currentWorkspace } = useCurrentWorkspace();
  const { data: storage } = useStorage();
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const [showPlansModal] = useAirModal(PlansModal);
  const { trackOpenIntercomChat } = useTrackOpenIntercomChat();

  const canEditPlan = canManageSubscription(workspacePermissions);
  const isOutOfStorage = storage?.isOutOfStorage;
  const isAlmostOutOfStorage = storage?.isAlmostOutOfStorage;

  const { isDismissed, dismiss } = useStorageLimitBannerDismiss({
    canEditPlan,
    isOutOfStorage: !!isOutOfStorage,
  });

  const handleUpgrade = useCallback(() => {
    showPlansModal();
  }, [showPlansModal]);

  const message = useMemo(() => {
    if (isOutOfStorage) {
      if (canEditPlan) {
        return 'Your workspace has reached its storage limit. Uploads are paused until you upgrade.';
      }
      return 'This workspace has reached its storage limit. Uploads are paused until your admin upgrades the plan.';
    }
    if (canEditPlan) {
      return "You're approaching your storage limit. Upgrade your plan to keep uploading without interruption.";
    }
    return 'Your workspace is approaching its storage limit. Notify your admin to upgrade and avoid upload interruptions.';
  }, [isOutOfStorage, canEditPlan]);

  const action = useMemo(() => {
    if (!canEditPlan) {
      return null;
    }

    if (isEnterprisePlan) {
      return (
        <button className="font-semibold text-inherit" onClick={trackOpenIntercomChat} type="button">
          Talk to support →
        </button>
      );
    }

    if (isBusinessPlan) {
      return (
        <a
          className="font-semibold text-inherit"
          href={Routes.enterprise.expansion}
          rel="noopener noreferrer"
          target={NEW_TAB}
        >
          Contact support →
        </a>
      );
    }

    return (
      <button className="font-semibold text-inherit" onClick={handleUpgrade} type="button">
        Upgrade now →
      </button>
    );
  }, [canEditPlan, isEnterprisePlan, isBusinessPlan, trackOpenIntercomChat, handleUpgrade]);

  if (!currentWorkspace || !storage || !members || (!isOutOfStorage && !isAlmostOutOfStorage) || isDismissed) {
    return null;
  }

  return (
    <AlertBanner
      color={isOutOfStorage ? 'red' : 'yellow'}
      data-testid="OUT_OF_STORAGE_BANNER"
      onDismiss={isOutOfStorage ? undefined : dismiss}
    >
      {message} {action}
    </AlertBanner>
  );
});

WorkspaceStorageStatusBanner.displayName = 'WorkspaceStorageStatusBanner';
